import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  from,
  Observable,
  of,
  map,
  catchError,
  first,
  firstValueFrom,
  switchMap,
} from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { Store } from '@ngrx/store';
import { UserState } from '../store/User/UserReducer';
import { initCsrfTokenSelector } from '../store/User/UserSelector';
import { Logout } from '../store/User/UserActions';
import { HttpStatusCode } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class CsrfService {
  constructor(
    private readonly spinnerService: NgxSpinnerService,
    private readonly store: Store<UserState>,
    private readonly router: Router,
  ) {}

  fetchWithCsrfGet(url: string, params: any = {}): Observable<any> {
    const queryString = new URLSearchParams(params).toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    console.log(`Constructed URL in fetchWithCsrfGet :: ${fullUrl}`);
    const headers: HeadersInit = {};

    let csrfToken: string | null = null;
    this.store
      .select(initCsrfTokenSelector)
      .pipe(
        map((token) => (csrfToken = token)),
        catchError((error) => {
          console.error('Error getting CSRF token from store:', error);
          return of(null);
        }),
      )
      .subscribe()
      .unsubscribe();

    if (csrfToken) {
      headers['X-XSRF-TOKEN'] = csrfToken;
    }
    console.debug(`Store csrf token = ${csrfToken}`);

    this.spinnerService.show();
    return from(
      fetch(fullUrl, {
        method: 'GET',
        credentials: 'include',
        redirect: 'follow',
        headers,
      })
        .then((response) => {
          this.spinnerService.hide();
          if (!response.ok) {
            this.handleHttpError(response, fullUrl);
            throw new Error(
              `Fetch failed: ${response.status} - ${response.statusText}`,
            );
          }
          return response.json();
        })
        .catch((err) => {
          console.error('Fetch error:', err);
          this.spinnerService.hide();
          throw err;
        }),
    );
  }

  fetchWithCsrfPost(url: string, data: any = {}): Observable<any> {
    console.log(`Sending POST request to: ${url}`);

    this.spinnerService.show();
    return from(fetch('/cw/api/csrf', { method: 'GET' })).pipe(
      switchMap((resp) => from(resp.json())),
      switchMap((csrf) => {
        console.debug(`Fetch result from csrf :: ${JSON.stringify(csrf)}`);

        if (!csrf.token) {
          throw new Error('Failed to get csrf token');
        }

        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          [csrf.headerName]: csrf.token,
        };

        return from(
          fetch(url, {
            method: 'POST',
            credentials: 'include',
            redirect: 'follow',
            headers,
            body: JSON.stringify(data),
          })
            .then((response) => {
              this.spinnerService.hide();
              if (!response.ok) {
                this.handleHttpError(response, url);
                throw new Error(
                  `Fetch failed: ${response.status} - ${response.statusText}`,
                );
              }
              return response.json();
            })
            .catch((err) => {
              console.error('Fetch error:', err);
              this.spinnerService.hide();
              throw err;
            }),
        );
      }),
    );
  }

  private handleHttpError(response: Response, requestUrl: string): void {
    if (this.isSessionInvalidError(response)) {
      this.handleSessionInvalidation(response.status, requestUrl);
    }
  }

  private isSessionInvalidError(response: Response): boolean {
    return response.status === HttpStatusCode.Unauthorized;
  }

  private handleSessionInvalidation(status: number, requestUrl: string): void {
    console.log(
      `Session invalidated (${status}) on ${requestUrl} - clearing authentication state`,
    );

    this.store.dispatch(new Logout());
    this.router.navigate(['/login']).then(() => {
      console.log('Redirected to login due to session invalidation');
    });
  }
}
